/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';
import { IPaymentConnectorPostMessage } from '../payment-instrument-message';

export interface IPaymentFrameElementProps {
    className?: string;
    iframeAriaLabel: string;
    displayContent?: string;
    sourceUrl?: string;
    requestUrlOrigin?: string;
    messageOrigin?: string;
    css?: string;
    height?: number;
    onIFrameMessage?(event: MessageEvent): void;
}

export const POST_MESSAGE_NAME = 'message';

const getHostName = (url: string = ''): string => {
    return (url.indexOf('//') > -1 ? url.split('/')[2] : url.split('/')[0]).toLowerCase();
};

/**
 *
 * IFrameElement component
 * @extends {React.PureComponent<IPaymentFrameElementProps>}
 */
class IPaymentFrameElement extends React.PureComponent<IPaymentFrameElementProps> {
    private iframeRef: React.RefObject<HTMLIFrameElement>;

    constructor(props: IPaymentFrameElementProps) {
        super(props);
        this.iframeRef = React.createRef();
    }

    public componentDidMount(): void {
        window.addEventListener(POST_MESSAGE_NAME, this.onEvent);

        this.updateContentDocument();
    }

    public componentDidUpdate(prevProps: IPaymentFrameElementProps): void {
        if (prevProps.displayContent !== this.props.displayContent) {
            this.updateContentDocument();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener(POST_MESSAGE_NAME, this.onEvent);
    }

    public render(): JSX.Element | null {
        const { sourceUrl, className, iframeAriaLabel, height } = this.props;
        return (
            <iframe
                className={classnames('checkout-payment-instrument__iframe', className)}
                aria-label={iframeAriaLabel}
                src={sourceUrl}
                ref={this.iframeRef}
                height={height}
                // tslint:disable-next-line:react-iframe-missing-sandbox
                sandbox='allow-scripts allow-forms allow-same-origin allow-popups'
            />
        );
    }

    public postMessage = (params: IPaymentConnectorPostMessage): void => {
        if (
            this.iframeRef &&
            this.iframeRef.current &&
            this.iframeRef.current.contentWindow &&
            this.iframeRef.current.contentWindow.postMessage
        ) {
            this.iframeRef.current.contentWindow.postMessage(params.message, params.targetOrigin);
        }
    };

    private onEvent = (event: MessageEvent) => {
        const { sourceUrl, onIFrameMessage, requestUrlOrigin, messageOrigin } = this.props;
        const sourceHost = getHostName(sourceUrl);
        const eventHost = getHostName(event.origin);
        const requestHost = getHostName(requestUrlOrigin);
        const messageHost = getHostName(messageOrigin);

        // Important: security check
        // check actual origin matches with expected origin
        if (
            !onIFrameMessage ||
            (sourceUrl && !(sourceHost === eventHost || requestHost === eventHost || messageHost === eventHost)) ||
            (!sourceUrl && !(requestHost === eventHost || messageHost === eventHost))
        ) {
            return;
        }

        return onIFrameMessage(event);
    };

    private updateContentDocument = (): void => {
        const { displayContent, sourceUrl, css } = this.props;
        if (!sourceUrl && displayContent && this.iframeRef && this.iframeRef.current) {
            const innerDocument = this.iframeRef.current.contentDocument;

            if (innerDocument) {
                innerDocument.open();
                innerDocument.write(displayContent);
                if (css) {
                    // Append custom style
                    const style = document.createElement('style');
                    const cssNote = document.createTextNode(css);
                    style.type = 'text/css';
                    style.appendChild(cssNote);
                    innerDocument.head.appendChild(style);
                }

                innerDocument.close();
            }
        }
    };
}

export default IPaymentFrameElement;